/**
 * Created by jonas on 2021-03-08.
 *
 * Utility class for a array that keeps track of a current index.
 * This makes it easy to get the next item on the list and it will wrap around.
 *
 * Optionally it can be randomized at wrap around.
 *
 * On creation the .current() will return the last object in the list. So if randomizeAtWrapAround is set
 * it will randomize the list on the fist use of next().
 *
 */
import {ArrayUtils} from "./ArrayUtils";

export class LoopableList<T> {
    private _index:number;
    private _list:T[];
    private _randomizeOnWrapAround:boolean = false;

    public get length():number {
        return this._list.length;
    };

    /**
     * @param list Non empty array of T
     */
    constructor(list:[T, ...T[]]) {
        if(list.length <= 0) {
            throw new Error("List can't be empty.");
        }
        this._list = list;
        this._index = this._list.length - 1;
    }

    private increment():number {
        this._index++;
        this._index %= this._list.length;
        if(this._index == 0 && this._randomizeOnWrapAround) {
            this._list = ArrayUtils.shuffleArray(this._list);
        }
        return this._index;
    }

    /**
     * Resets index so that .next() will return the first element in the list,
     * .current() would return the last object in the list.
     */
    public reset() {
        this._index = this._list.length - 1;
    }

    /**
     * Returns the next item on the list
     */
    public next():T {
        const index = this.increment()
        return this._list[index];
    }

    /**
     * Returns the current value of the list.
     */
    public current():T {
        return this._list[this._index];
    }


    /**
     * Sets randomizeOnWrapAround, it also returns it self so the call can be chained with it's creation. Eg.
     *
     * const loopableList<number> = new LoopableList([1,2,3]).setRandomizeAtWrapAround(true);
     *
     * @param randomize
     * @return LoopableList - Self.
     */
    public setRandomizeAtWrapAround(randomize:boolean):LoopableList<T> {
        this._randomizeOnWrapAround = randomize;
        return this;
    }

    /**
     * Returns a copy of the array that is the base of the list and optionally shuffles it before returning.
     * @param randomize
     */
    public getListClone(randomize:boolean):T[] {
        if(randomize) {
            return ArrayUtils.shuffleArray(this._list);
        }
        return this._list.concat();
    }
}